import React from "react";
import { Route } from "react-router-dom";
import { UserRoleEnum } from "@/types/role.type";
import type { RouteCtx } from "../routeContext";
import { PlaceholderPage, NotFound } from "../shell";
import {
  Dashboard,
  MonitoringTracker,
  SiteActivation,
  RegulatoryApprovalsMain,
  ApprovalDetailPage,
  MyTasksCTMS,
  IssueRiskTrackerPage,
  SiteFeasibilityPage,
  RBQMCenter,
} from "../lazyPages";

/** craRoutes — order preserved for React Router first-match. */
export function craRoutes(ctx: RouteCtx): React.ReactNode {
  const {
    userRole,
    selectedStudyId,
    availableStudies,
    hasRbqmAccess,
    currentStudy,
    onLogout,
  } = ctx;

  if (userRole !== UserRoleEnum.CRA) {
    return null;
  }

  return (
    <>
      {/* Dashboards */}
      <Route
        path="/cra/dashboard"
        element={<Dashboard onLogout={onLogout} />}
      />
      <Route
        path="/cra/sites/dashboard"
        element={<PlaceholderPage title="Site Dashboard" />}
      />

      {/* CTMS Monitoring */}
      <Route
        path="/cra/tasks"
        element={
          <MyTasksCTMS
            userRole={userRole}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/cra/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/cra/ctms/visit-reports"
        element={<PlaceholderPage title="Visit Reports" />}
      />
      <Route
        path="/cra/ctms/action-items"
        element={<PlaceholderPage title="Action Items" />}
      />
      <Route
        path="/cra/site-activation"
        element={
          <SiteActivation userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/cra/site-feasibility"
        element={
          <SiteFeasibilityPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/cra/regulatory"
        element={
          <RegulatoryApprovalsMain
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/cra/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />

      {/* EDC Review */}
      <Route
        path="/cra/edc/review"
        element={<PlaceholderPage title="eCRF Review" />}
      />
      <Route
        path="/cra/edc/queries"
        element={<PlaceholderPage title="Query Management" />}
      />
      <Route
        path="/cra/edc/sdv"
        element={<PlaceholderPage title="Source Data Verification (SDV/SDR)" />}
      />

      {/* Documents */}
      <Route
        path="/cra/docs/monitoring"
        element={<PlaceholderPage title="Monitoring Docs" />}
      />
      <Route
        path="/cra/docs/site-essentials"
        element={<PlaceholderPage title="Site Essential Documents" />}
      />

      {/* IWRS */}
      <Route
        path="/iwrs/randomization"
        element={<PlaceholderPage title="Randomization" />}
      />
      <Route
        path="/iwrs/inventory"
        element={<PlaceholderPage title="Inventory & Kits" />}
      />
      <Route
        path="/iwrs/kit-assignment"
        element={<PlaceholderPage title="Kit Assignment" />}
      />
      <Route
        path="/iwrs/unblinding"
        element={<PlaceholderPage title="Unblinding" />}
      />
      <Route
        path="/iwrs/supply-management"
        element={<PlaceholderPage title="Supply Management" />}
      />
      <Route
        path="/iwrs/dashboards"
        element={<PlaceholderPage title="IWRS Dashboards" />}
      />

      {/* Audit & Compliance */}
      <Route
        path="/cra/audit/logs"
        element={<PlaceholderPage title="Audit Logs (Study/Site)" />}
      />
      <Route
        path="/cra/risk-tracker"
        element={
          <IssueRiskTrackerPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/cra/rbqm-center"
        element={
          hasRbqmAccess ? (
            <RBQMCenter userRole={userRole} currentStudy={currentStudy} />
          ) : (
            <NotFound />
          )
        }
      />
    </>
  );
}
